import {
  LEAD_TYPE_LABEL,
  Lead,
  LeadType,
  LeadTypeChangeLockedReason
} from "views/leads-id/types";

export const getCreatableLeadTypes = ({
  isTenantLeads,
  isOwnerLeads,
  isShowings,
  hasEntityContext
}: {
  isTenantLeads: boolean;
  isOwnerLeads: boolean;
  isShowings: boolean;
  hasEntityContext: boolean;
}): LeadType[] => {
  const canCreateOwner = isOwnerLeads && !hasEntityContext;
  const canCreateTenant = isTenantLeads || !canCreateOwner;
  return [
    ...(canCreateTenant ? [LeadType.Tenant] : []),
    ...(canCreateOwner ? [LeadType.Owner] : []),
    ...(isShowings ? [LeadType.Realtor] : [])
  ];
};

const LEAD_TYPE_LOCKED_REASON_COPY: Record<LeadTypeChangeLockedReason, string> =
  {
    application_started:
      "Type is locked because this lead has started a rental application",
    owner_lead: "Owner leads can't be changed to another type"
  };

const SWITCHABLE_LEAD_TYPES = [LeadType.Tenant, LeadType.Realtor];

const toOption = (leadType: LeadType) => ({
  label: LEAD_TYPE_LABEL[leadType],
  value: leadType
});

const getLockedReason = (
  lead: Pick<
    Lead,
    | "lead_type"
    | "lead_type_change_locked_reason"
    | "application_id"
    | "applicant_id"
    | "application"
  >
): LeadTypeChangeLockedReason | null => {
  if (lead.lead_type_change_locked_reason)
    return lead.lead_type_change_locked_reason;
  if (lead.lead_type === LeadType.Owner) return "owner_lead";
  if (
    lead.lead_type === LeadType.Tenant &&
    Boolean(lead.application_id || lead.applicant_id || lead.application)
  )
    return "application_started";
  return null;
};

export const getLeadTypeSwitchState = (
  lead: Pick<
    Lead,
    | "lead_type"
    | "lead_type_change_locked_reason"
    | "application_id"
    | "applicant_id"
    | "application"
  >,
  { isShowings }: { isShowings: boolean }
): {
  canSwitch: boolean;
  options: { label: string; value: LeadType }[];
  lockedReason: string | null;
} => {
  if (lead.lead_type === LeadType.Owner)
    return {
      canSwitch: false,
      options: [toOption(LeadType.Owner)],
      lockedReason: LEAD_TYPE_LOCKED_REASON_COPY.owner_lead
    };

  const lockedReason = getLockedReason(lead);
  return {
    canSwitch: isShowings && lockedReason === null,
    options: SWITCHABLE_LEAD_TYPES.map(toOption),
    lockedReason: lockedReason
      ? LEAD_TYPE_LOCKED_REASON_COPY[lockedReason]
      : null
  };
};
